import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import DemoPageChrome from "../../../components/demo/DemoPageChrome";
import DemoProgress from "../../../components/demo/DemoProgress";
import DemoThemedShell from "../../../components/demo/DemoThemedShell";
import { ReadableCard } from "../../../components/Sticker";
import { GAME_PHASES, phaseTitle } from "../../core/phases.js";
import {
  activateDemoSession,
  getDemoCheckedIn,
  getDemoFunnelProgress,
  getTodaysDemoEvent,
  readDemoState,
  resetDemo,
  writeDemoStateForGame,
} from "../../../lib/demoExperience.js";
import { CREW_NUDGE_MIN_DISTINCT, EVENT_SIGNAL_MIN_APPRECIATIONS, FD_VOTE_TITLE, FD_WELCOME_TITLE } from "../copy.js";
import { createDemoFavoriteDancesActions } from "../demo/demoState.js";
import { useFavoriteDancesSession } from "../hooks/useFavoriteDancesSession.js";
import DeclineStep from "../steps/DeclineStep.jsx";
import PlayStep from "../steps/PlayStep.jsx";
import ResultsStep from "../steps/ResultsStep.jsx";
import WelcomeStep from "../steps/WelcomeStep.jsx";

function getWelcomeAttendees(state) {
  if (!state?.my_rsvp) return [];
  return getDemoCheckedIn(state).filter((d) => d.dancer_id !== state.my_rsvp.dancer_id);
}

export default function DemoVotePage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const nav = useNavigate();
  const event = useMemo(() => getTodaysDemoEvent(), []);
  const [state, setState] = useState(() => readDemoState());

  useEffect(() => {
    activateDemoSession(searchParams.get("demo") === "true");
  }, [searchParams]);

  useEffect(() => {
    if (!state?.my_rsvp) {
      nav("/demo?demo=true", { replace: true });
      return;
    }
    if (state.checked_in_at) {
      toast.message("Check out from the floor first", {
        description: "Tap Leave floor & vote on the check-in screen.",
      });
      nav("/demo/check-in?demo=true", { replace: true });
      return;
    }
    if (!state.floor_attended_at) {
      toast.message("Complete check-in first", {
        description: "RSVP and check in on the demo event page.",
      });
      nav("/demo?demo=true", { replace: true });
    }
  }, [state, nav]);

  const demoActions = useMemo(
    () =>
      createDemoFavoriteDancesActions({
        getState: () => readDemoState(),
        setState,
        getWelcomeAttendees,
        writeState: writeDemoStateForGame,
      }),
    [],
  );

  const session = useFavoriteDancesSession({
    enabled: Boolean(state?.my_rsvp && !state?.checked_in_at && state?.floor_attended_at),
    dataSource: "demo",
    initialDemoState: state,
    demoActions,
    onSubmitted: () => toast.success("Votes saved — thanks for trying the demo!"),
  });

  const { phase, selected, busy, optIn, decline, submit, togglePick } = session;
  const voteAttendees = demoActions.getVoteAttendees(state);
  const funnel = getDemoFunnelProgress(state, location.pathname);
  const sheetTitle = phaseTitle(phase, {
    welcomeTitle: FD_WELCOME_TITLE,
    playTitle: FD_VOTE_TITLE,
  });
  const showChrome = phase !== GAME_PHASES.RESULTS && phase !== GAME_PHASES.DECLINED;

  if (!state?.my_rsvp || state.checked_in_at || !state.floor_attended_at) {
    return null;
  }

  return (
    <DemoThemedShell className={`max-w-page mx-auto px-4 pt-5 ${phase === GAME_PHASES.RESULTS ? "pb-0" : "pb-12"}`}>
      {showChrome && (
        <>
          <DemoPageChrome backTo="/demo?demo=true" backLabel="Event" />
          <DemoProgress current={funnel.current} completed={funnel.completed} />
        </>
      )}

      {phase !== GAME_PHASES.RESULTS && phase !== GAME_PHASES.DECLINED && (
        <ReadableCard className="p-4 mb-5">
          <p className="font-ui text-[12px] font-medium text-ink/65">After the social</p>
          <h1 className="font-ui font-semibold text-[22px] sm:text-[24px] leading-tight mt-0.5">{sheetTitle}</h1>
          <p className="mt-1.5 text-[14px] font-medium text-ink/75">{event.name}</p>
        </ReadableCard>
      )}

      {phase === GAME_PHASES.WELCOME && (
        <WelcomeStep
          attendees={getWelcomeAttendees(state)}
          eventTotalPicks={demoActions.getEventTotalPicks(state)}
          onOptIn={optIn}
          onDecline={decline}
          busy={busy}
          hideTitle
          compactCopy
          showHowItWorks={false}
        />
      )}

      {phase === GAME_PHASES.PLAY && (
        <PlayStep
          attendees={voteAttendees}
          selected={selected}
          maxPicks={3}
          onToggle={togglePick}
          onSubmit={submit}
          submitting={busy}
          stickyFooter
          submitTestId="demo-vote-submit"
        />
      )}

      {phase === GAME_PHASES.RESULTS && (
        <div className="min-h-[calc(100dvh-1.5rem)] flex flex-col" data-testid="demo-vote-done">
          <div className="flex-1">
            <ResultsStep
              myAppreciationsReceived={demoActions.getMyAppreciationsReceived(state)}
              myPickCount={state?.votee_ids?.length ?? 0}
              eventTotalPicks={demoActions.getEventTotalPicks(state)}
              eventSignalMinAppreciations={EVENT_SIGNAL_MIN_APPRECIATIONS}
              crewDistinctCount={demoActions.getCrewDistinctCount(state)}
              crewNudgeMinDistinct={CREW_NUDGE_MIN_DISTINCT}
              onDone={() => resetDemo({ redirectTo: "/" })}
              doneTestId="demo-vote-back-home"
              doneLabel="Back to Home"
              stickyFooter
            />
          </div>
        </div>
      )}

      {phase === GAME_PHASES.DECLINED && (
        <DeclineStep
          onClose={() => nav("/demo?demo=true")}
          closeTestId="demo-vote-decline-done"
          closeLabel="Back to event"
        />
      )}
    </DemoThemedShell>
  );
}
